'use client';

import { ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { usePathname, useRouter } from 'next/navigation';
import { cn } from '@utils/cn';
import { TwoColumnLayout } from '@components/layout/Layouts/TwoColumnLayout';
import { CandidateJobsSidebar } from '@components/features/candidate-jobs-sidebar';
import { CompanyJobsSidebar } from '@components/features/company-jobs-sidebar/CompanyJobsSidebar';
import { SidebarNavigationProvider, useSidebarNavigation } from '@components/contexts/SidebarNavigationContext';
import { FilterDrawerProvider } from '@components/contexts/FilterDrawerContext';
import { MissionControlBestieChatProvider } from '@components/contexts/MissionControlBestieChatContext';
import { McSharedStateProvider, useMcSharedState } from '@components/contexts/McSharedStateContext';
import { CompanyApplicantProvider } from '@components/contexts/CompanyApplicantContext';
import { CandidatePromptBar } from '@components/features/candidate-prompt-bar';
import { useMissionControlContext, type MissionControlContext } from '@hooks/features/useMissionControlContext';
import { getI18nLinkHref } from '@services/i18nPaths';
import type { JobSearchQueryParams } from '@type/filters';

const googleApiKey = process.env.GOOGLE_MAPS_CLIENT_API_KEY as string;

// Pages that render their OWN bottom prompt, so the shell must not add a second bar:
// /mc/job-add, /mc/company, /mc/cv/search (collapsedFab mission chips) and /mc/my-jobs (its own
// McTalentSearchPrompt — the CV-search bar).
const PAGE_RENDERS_OWN_PROMPT_BAR = /\/mc\/(job-add|company|cv\/search|my-jobs)(\/|$)/;
// Company applicant/flow pages (/mc/job/{slug}/applicants[/...]) render CompanyFloatingNavBar
// (also fixed at the bottom), so the company shell's prompt bar is suppressed there to avoid a
// collision. Narrow to `/applicants` so candidate job-results (/mc/job/list/...) are NOT caught.
const PAGE_RENDERS_COMPANY_FLOATING_NAV = /\/mc\/job\/[^/]+\/applicants/;
// Shell-hosted areas that should show NO prompt bar at all (the wallet area). Everything else
// hosted in the shell (the root `/`, `/mc`, results, …) shows it.
const PAGE_HIDES_PROMPT_BAR = /\/wallet(\/|$)/;
// Standalone job detail page (`/mc/job/[slug]`, ro `/mc/loc-de-munca/[slug]`) — the fixed apply
// button bar owns the bottom of the screen there, so the prompt bar would overlap it. Excludes
// the untranslated list path (`/mc/job/list`) and any sub-pages (applicants, after-apply, …).
const PAGE_IS_JOB_DETAIL = /\/mc\/(?:job|loc-de-munca)\/(?!list(?:\/|$))[^/]+\/?$/;
// Edit pages (profile/CV editor) where the prompt input is not useful: show only the mission
// chips instead of the full bar (same as /mc/company/edit, which renders its own collapsedFab).
const PAGE_PROMPT_BAR_CHIPS_ONLY = /\/mc\/(profile|cv)(\/|$)/;

interface McShellProps {
  children: ReactNode;
  locale: Locale;
  isAuthenticated: boolean;
  /** Server-derived context for the first paint (cookie/wallet), to avoid a candidate↔company flash. */
  initialContext: MissionControlContext;
}

/** Dispatches close events for sidebar + detail modal on mobile. */
const dispatchMobileClose = () => {
  if (typeof window !== 'undefined' && window.innerWidth < 1024) {
    window.dispatchEvent(new Event('close-sidebar'));
    window.dispatchEvent(new Event('close-detail-modal'));
  }
};

// Candidate providers are hoisted ABOVE the inner shell so candidate content
// (e.g. /mc/bestie-chat, which renders JobListContent and requires SidebarNavigationProvider)
// can render in either context. This makes pages shell-agnostic: mission selection navigates
// without switching context, and a candidate page opened from the company context no longer
// crashes for want of its providers. The state is cheap/local, so always mounting it for
// company users is harmless. CompanyApplicantProvider is hoisted for the same reason — it backs
// the company sidebar/pages and its state is cheap, so it stays mounted in candidate context too.
const McShellProviders = ({ children, locale }: { children: ReactNode; locale: Locale }) => (
  <SidebarNavigationProvider locale={locale}>
    <FilterDrawerProvider>
      <MissionControlBestieChatProvider>
        <McSharedStateProvider locale={locale}>
          <CompanyApplicantProvider>
            {children}
          </CompanyApplicantProvider>
        </McSharedStateProvider>
      </MissionControlBestieChatProvider>
    </FilterDrawerProvider>
  </SidebarNavigationProvider>
);

// ---------------------------------------------------------------------------
// Inner shell — a SINGLE TwoColumnLayout that stays mounted across context switches.
// Switching context (candidate ↔ company) only swaps the `leftSidebar` element, so the new
// sidebar mounts and refreshes (showing its own loading state) while the `mainContent` — the
// page (`children`) plus the shared prompt bar — is preserved in place and NOT remounted. This
// is a client-side state flip (cookie + React state via setContext), with no navigation and no
// full reload, which is why the main content stays exactly as it was.
// ---------------------------------------------------------------------------
interface McShellInnerProps extends Omit<McShellProps, 'initialContext'> {
  currentContext: MissionControlContext;
  setContext: (next: MissionControlContext) => void;
}

const McShellInner = ({
  children, locale, isAuthenticated, currentContext, setContext,
}: McShellInnerProps) => {
  const router = useRouter();
  const pathname = usePathname() ?? '';
  const { currentView, navigateToCandidateSearch } = useSidebarNavigation();
  const { submitChat } = useMcSharedState();

  const isCompany = currentContext === 'company';

  // Soft context switch: flip the cookie + React state. No navigation, no reload — only the
  // sidebar swaps; the main content stays as it is. The mobile sidebar is intentionally NOT
  // closed here so the user can keep interacting with the (newly swapped) sidebar.
  const handleSwitchContext = useCallback(() => {
    setContext(isCompany ? 'candidate' : 'company');
  }, [isCompany, setContext]);

  // Candidate sidebar: open the Jobs on Map modal. Dynamic import avoids a static cycle.
  const handleOpenMap = useCallback((queryParams?: Partial<JobSearchQueryParams>) => {
    if (!googleApiKey) return;
    void (async () => {
      const { showJobsMapModal } = await import('@components/templates/job/list/JobsMapModal');
      showJobsMapModal({ googleApiKey, locale, queryParams });
    })();
  }, [locale]);

  // Company sidebar: /mc/job/{slug}/... → highlight that job.
  const jobMatch = pathname.match(/\/mc\/job\/([^/]+)/);
  const selectedJobSlug = jobMatch ? jobMatch[1] : undefined;

  // CompactJobCard's links are plain <a> (full reload). These callbacks intercept them and
  // soft-navigate instead — same destinations as the card hrefs but without remounting the shell.
  const handleJobSelect = useCallback((job: ApiSchema<'McMyJobCard'>) => {
    dispatchMobileClose();
    router.push(getI18nLinkHref(`/mc/job/${job.slug}/applicants/input-flow`, locale));
  }, [router, locale]);

  const handleTagClick = useCallback((job: ApiSchema<'McMyJobCard'>, status: number) => {
    dispatchMobileClose();
    const base = getI18nLinkHref(`/mc/job/${job.slug}/applicants`, locale);
    router.push(`${base}?status=${status}&withPlaceholders=true`);
  }, [router, locale]);

  // The prompt bar is identical in both contexts (mode derived from the view: search on job
  // search, chat on coaching), hidden on the wallet area / job detail / own-bar / flow pages.
  const showPromptBar = currentView !== 'my-bestie-landing'
    && !PAGE_HIDES_PROMPT_BAR.test(pathname)
    && !PAGE_IS_JOB_DETAIL.test(pathname)
    && !PAGE_RENDERS_OWN_PROMPT_BAR.test(pathname)
    && !PAGE_RENDERS_COMPANY_FLOATING_NAV.test(pathname);
  const showChipsOnly = showPromptBar && PAGE_PROMPT_BAR_CHIPS_ONLY.test(pathname);

  const leftSidebar = isCompany ? (
    <CompanyJobsSidebar
      locale={locale}
      isAuthenticated={isAuthenticated}
      selectedJobSlug={selectedJobSlug}
      onJobSelect={handleJobSelect}
      onTagClick={handleTagClick}
      onSwitchContext={handleSwitchContext}
    />
  ) : (
    <CandidateJobsSidebar
      locale={locale}
      isAuthenticated={isAuthenticated}
      onSwitchContext={handleSwitchContext}
      onOpenMap={handleOpenMap}
    />
  );

  return (
    <TwoColumnLayout
      leftSidebar={leftSidebar}
      mainContent={(
        <div className={cn('flex min-h-full flex-col bg-surface-100', showPromptBar && 'pb-28 lg:pb-24')}>
          {children}
          {showPromptBar && (
            <CandidatePromptBar
              locale={locale}
              isAuthenticated={isAuthenticated}
              onSearchSubmit={navigateToCandidateSearch}
              onChatSubmit={submitChat}
              collapsedFab={showChipsOnly}
            />
          )}
        </div>
      )}
      rightSidebar={undefined}
    />
  );
};

/**
 * @description Persistent Mission Control shell. Mounted once by mc/layout for the whole
 * `/mc/*` subtree so the sidebar + providers + (candidate) chat/prompt survive navigation —
 * only the page content (`children`) changes per route. Renders the candidate or company
 * shell based on the current context (seeded by the server-derived `initialContext`).
 */
export const McShell = ({
  children, locale, isAuthenticated, initialContext,
}: McShellProps) => {
  const { t } = useTranslation('common');
  const { currentContext, setContext } = useMissionControlContext({ initialContext });

  useEffect(() => {
    const isCompany = currentContext === 'company';
    document.title = isCompany
      ? t('bestie.hire.meta.title', 'BestJobs Hire – Candidați intervievați, rezultate garantate')
      : t('homepage.react.meta.title', 'BestJobs');
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', isCompany
        ? t('bestie.hire.meta.description', 'Bestie este agentul tău AI de recrutare.')
        : t('meta.homepage.description_react', 'BestJobs – Stop searching, start interviewing!'));
    }
  }, [currentContext, t]);

  return (
    <McShellProviders locale={locale}>
      <McShellInner
        locale={locale}
        isAuthenticated={isAuthenticated}
        currentContext={currentContext}
        setContext={setContext}
      >
        {children}
      </McShellInner>
    </McShellProviders>
  );
};
